import { AlertCircle, CheckCircle, Info } from 'lucide-react';

type AlertVariant = 'error' | 'success' | 'info';

interface AlertModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onClose: () => void;
  variant?: AlertVariant;
}

/**
 * Acknowledge ("OK") dialog. Dark-mode aware (Tailwind `dark:` classes — inert
 * unless the `dark` class is on <html>, so safe for both the light admin app
 * and the dark-capable affiliate portal).
 *
 * Replaces native browser alert().
 */
export function AlertModal({ isOpen, title, message, onClose, variant = 'error' }: AlertModalProps) {
  if (!isOpen) return null;

  const accent = {
    error: { Icon: AlertCircle, color: 'text-red-600 dark:text-red-400' },
    success: { Icon: CheckCircle, color: 'text-green-600 dark:text-green-400' },
    info: { Icon: Info, color: 'text-blue-600 dark:text-blue-400' },
  }[variant];
  const { Icon, color } = accent;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

      <div className="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <Icon className={`w-6 h-6 flex-shrink-0 ${color}`} />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
        </div>

        <div className="text-gray-700 dark:text-slate-300 mb-6 whitespace-pre-line text-sm">
          {message}
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
